<template>
  <div style="overflow:hidden;">
    <div class="search-bar">
      <Input v-model="searchQuery" placeholder="搜索项目名称" clearable style="width: 300px" />
      <Button type="primary" @click="handleSearch" style="margin-left: 10px">搜索</Button>
      <Button type="success" @click="handleAdd" style="margin-left: 10px">新建项目</Button>
    </div>

    <div class="status-filter">
      <div
        class="status-item"
        :class="{'active': currentState === 'all'}"
        @click="handleStateChange('all')"
      >
        全部
        <span class="count">{{ allProjectsCount }}</span>
      </div>
      <div
        v-for="state in stateOptions"
        :key="state.value"
        class="status-item"
        :class="{'active': currentState === state.value}"
        @click="handleStateChange(state.value)"
      >
        {{ state.label }}
        <span class="count">{{ getStateCount(state.value) }}</span>
      </div>
    </div>

    <div class="table-wrapper">
      <BaseTable
        :columns="columns"
        :data="filteredProjects"
        :loading="loading"
        :count="total"
        paged
        :page="page"
        :pageSize="pageSize"
        border
        @event="handleTableEvent"
      ></BaseTable>
    </div>

    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      @on-ok="handleSubmit"
      :loading="confirmLoading"
    >
      <Form ref="formRef" :model="formData" :rules="rules">
        <div class="form-section">
          <BaseBoardSubTitle name="基本信息" />
          <FormItem label="名称" name="name" required>
            <Input v-model="formData.name" placeholder="请输入项目名称" maxlength="32" />
          </FormItem>
          <FormItem label="讲师" name="charger" required>
            <BaseUserSelect v-model="formData.charger" placeholder="请选择讲师" />
          </FormItem>
          <FormItem label="描述" name="desc">
            <Input v-model="formData.desc" type="textarea" :rows="4" placeholder="请输入项目描述" maxlength="255" />
          </FormItem>
          <FormItem label="封面图片" name="avatar">
            <BaseImageUpload v-model="formData.avatar" :option="{single:true}" />
          </FormItem>
        </div>

        <div class="form-section">
          <BaseBoardSubTitle name="时间与状态" />
          <div class="form-row">
            <FormItem label="开始日期" name="started_at" class="form-item" required>
              <DatePicker v-model="formData.started_at" type="date" placeholder="选择开始日期" style="width: 100%" />
            </FormItem>
            <FormItem label="结束日期" name="finished_at" class="form-item">
              <DatePicker v-model="formData.finished_at" type="date" placeholder="选择结束日期" style="width: 100%" />
            </FormItem>
          </div>
          <FormItem label="状态" name="state">
            <Select v-model="formData.state" style="width: 100%">
              <Option v-for="state in stateOptions" :key="state.value" :value="state.value">{{ state.label }}</Option>
            </Select>
          </FormItem>
        </div>
      </Form>
    </Modal>

    <!-- 项目详情抽屉 -->
    <Drawer
      v-model="drawerVisible"
      width="650"
      :title="currentProject ? currentProject.name : '项目详情'"
    >
      <div v-if="currentProject" class="project-detail">
        <div class="detail-header">
          <div class="project-avatar">
            <img v-if="currentProject.avatar" :src="currentProject.avatar" :alt="currentProject.name">
            <div v-else class="avatar-placeholder">{{ currentProject.name.substring(0, 1) }}</div>
          </div>
          <div class="project-info">
            <h2>{{ currentProject.name }}</h2>
            <div class="info-row">
              <Tag :color="getStateColor(currentProject.state)">{{ getStateLabel(currentProject.state) }}</Tag>
              <span class="created-time">创建于 {{ formatDateTime(currentProject.created_at) }}</span>
            </div>
            <div class="project-desc">{{ currentProject.desc || '暂无描述' }}</div>
          </div>
        </div>

        <div class="detail-content">
          <div class="info-section">
            <div class="info-item">
              <span class="label">讲师：</span>
              <span class="value"><BaseNameBoard :uid="currentProject.charger" /></span>
            </div>
            <div class="info-item">
              <span class="label">开始日期：</span>
              <span class="value">{{ formatDate(currentProject.started_at) }}</span>
            </div>
            <div class="info-item">
              <span class="label">结束日期：</span>
              <span class="value">{{ formatDate(currentProject.finished_at) || '未设置' }}</span>
            </div>
            <div class="info-item">
              <span class="label">关联课程：</span>
              <span class="value">{{ currentProject.count || 0 }} 个</span>
            </div>
          </div>

          <Divider>课程列表</Divider>

          <div v-if="classesLoading" class="loading-classes">
            <Spin size="large" fix></Spin>
          </div>

          <div v-else-if="projectClasses.length === 0" class="empty-classes">
            <div class="empty-placeholder">
              <Icon type="ios-school" size="48" />
              <p>暂无课程</p>
              <Button type="primary" icon="ios-add-circle-outline" @click="handleAddCourse" style="margin-top: 16px">新增课程</Button>
            </div>
          </div>

          <div v-else class="classes-cards">
            <!-- Add New Course Card -->
            <Card class="course-card add-course-card" @click="handleAddCourse">
              <div class="add-course-content">
                <Icon type="ios-add-circle-outline" size="48" />
                <p>添加课程</p>
              </div>
            </Card>

            <!-- Course Cards -->
            <Card v-for="course in projectClasses" :key="course.id" class="course-card">
              <div class="course-header">
                <div class="course-title">{{ course.name }}</div>
                <Tag :color="getStateColor(course.state)">{{ getStateLabel(course.state) }}</Tag>
              </div>

              <div class="course-info">
                <div class="course-info-item">
                  <Icon type="ios-person" />
                  <span>讲师：</span>
                  <BaseNameBoard :uid="course.charger" />
                </div>

                <div class="course-info-item">
                  <Icon type="ios-time" />
                  <span>开始时间：{{ formatDateTime(course.started_at) }}</span>
                </div>

                <div v-if="course.duration" class="course-info-item">
                  <Icon type="ios-timer" />
                  <span>时长：{{ course.duration }} 分钟</span>
                </div>

                <div v-if="course.address" class="course-info-item">
                  <Icon type="ios-pin" />
                  <span>地点：{{ course.address }}</span>
                </div>

                <div v-if="course.video_url" class="course-info-item">
                  <Icon type="ios-videocam" />
                  <span>视频文件：</span>
                  <a :href="course.video_url" target="_blank">查看视频</a>
                </div>

                <div class="course-info-item">
                  <Icon type="ios-create" />
                  <span>创建者：</span>
                  <BaseNameBoard :uid="course.created_by" />
                </div>
              </div>

              <div v-if="course.desc" class="course-desc">
                <Tooltip :content="course.desc" placement="bottom" max-width="400">
                  <div class="desc-content">{{ course.desc }}</div>
                </Tooltip>
              </div>

              <!-- Course Action Buttons -->
              <div class="course-actions">
                <Button type="primary" size="small" icon="ios-create-outline" @click.stop="handleEditCourse(course)">修改</Button>
                <Button type="error" size="small" icon="ios-trash-outline" @click.stop="handleDeleteCourse(course)">删除</Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Drawer>

    <!-- Course Form Modal -->
    <Modal
      v-model="courseModalVisible"
      :title="courseModalTitle"
      @on-ok="handleCourseSubmit"
      :loading="courseConfirmLoading"
    >
      <Form ref="courseFormRef" :model="courseFormData" :rules="courseRules">
        <BaseBoardSubTitle name="课程基本信息" />
        <FormItem label="课程名称" prop="name" required>
          <Input v-model="courseFormData.name" placeholder="请输入课程名称" maxlength="32" />
        </FormItem>
        <FormItem label="讲师" prop="charger" required>
          <BaseUserSelect v-model="courseFormData.charger" placeholder="请选择讲师" />
        </FormItem>
        <FormItem label="描述" prop="desc">
          <Input v-model="courseFormData.desc" type="textarea" :rows="4" placeholder="请输入课程描述" maxlength="255" />
        </FormItem>
        <FormItem label="上课地点" prop="address">
          <Input v-model="courseFormData.address" placeholder="请输入上课地点" maxlength="255" />
        </FormItem>

        <BaseBoardSubTitle name="时间与状态" />
        <FormItem label="开始时间" prop="started_at" required>
          <DatePicker v-model="courseFormData.started_at" type="datetime" placeholder="选择开始时间" style="width: 100%" />
        </FormItem>
        <FormItem label="时长(分钟)" prop="duration">
          <InputNumber v-model="courseFormData.duration" :min="0" placeholder="请输入课程时长" style="width: 100%" />
        </FormItem>
        <FormItem label="状态" prop="state">
          <Select v-model="courseFormData.state" style="width: 100%">
            <Option v-for="state in stateOptions" :key="state.value" :value="state.value">{{ state.label }}</Option>
          </Select>
        </FormItem>

        <BaseBoardSubTitle name="媒体文件" />
        <FormItem label="视频文件" prop="video_url">
          <BaseFileUpload v-model="courseFormData.video_url" accept="video/*" />
        </FormItem>
        <FormItem label="封面图片" prop="avatar">
          <BaseImageUpload v-model="courseFormData.avatar" :option="{single:true}" />
        </FormItem>
      </Form>
    </Modal>
  </div>
</template>

<script>
import {orderBy} from 'lodash';

export default {
  data() {
    return {
      projects: [],
      searchQuery: '',
      currentState: 'all',
      loading: false,
      confirmLoading: false,
      modalVisible: false,
      modalTitle: '添加项目',
      currentId: null,
      total: 0,
      page: 1,
      pageSize: 50,
      formData: this.getInitialFormData(),

      // 项目详情抽屉
      drawerVisible: false,
      currentProject: null,
      projectClasses: [],
      classesLoading: false,

      // 课程表单
      courseModalVisible: false,
      courseModalTitle: '添加课程',
      courseConfirmLoading: false,
      currentCourseId: null,
      courseFormData: this.getInitialCourseFormData(),

      stateOptions: [
        {label: '未开始', value: 0},
        {label: '进行中', value: 1},
        {label: '已结束', value: 2},
      ],
      stateCount: {},

      columns: [
        {type: 'index', width: 80, title: '序号'},
        {
          title: '项目',
          key: 'name',
          render: (h, params) => {
            return h('div', {
              style: {
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              },
              on: {
                click: () => {
                  this.showProjectDetail(params.row);
                },
              },
            }, [
              h('div', {
                style: {
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  margin: '5px',
                  marginRight: '10px',
                  background: '#f0f0f0',
                  flexShrink: 0,
                },
              }, [
                params.row.avatar ? h('img', {
                  attrs: {
                    src: params.row.avatar,
                    alt: params.row.name,
                  },
                  style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  },
                }) : h('div', {
                  style: {
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    color: '#999',
                  },
                }, params.row.name.substring(0, 1)),
              ]),
              h('div', [
                h('div', {
                  style: {
                    fontWeight: 'bold',
                    textAlign: 'left',
                    color: '#1890ff',
                  },
                }, params.row.name),

              ]),
            ]);
          },
        },
        {
          title: '讲师',
          key: 'charger',
          type: 'user',
          width: 120,
        },
        {
          title: '已报名',
          key: 'count',
          width: 80,
          align: 'center',
        },
        {
          title: '开始日期',
          key: 'started_at',
          width: 150,
          render: (h, params) => {
            return h('span', this.formatDate(params.row.started_at));
          },
        },
        {
          title: '结束日期',
          key: 'finished_at',
          width: 150,
          render: (h, params) => {
            return h('span', this.formatDate(params.row.finished_at));
          },
        },
        {
          title: '状态',
          key: 'state',
          width: 100,
          render: (h, params) => {
            const stateMap = {
              0: {color: 'blue', text: '未开始'},
              1: {color: 'green', text: '进行中'},
              2: {color: 'default', text: '已结束'},
            };

            const state = stateMap[params.row.state] || {color: 'default', text: params.row.state || '未知'};

            return h('Tag', {
              props: {
                color: state.color,
              },
            }, state.text);
          },
        },
        {
          title: '创建时间',
          key: 'created_at',
          width: 150,
          render: (h, params) => {
            return h('span', this.formatDateTime(params.row.created_at));
          },
        },
        {
          title: '操作',
          key: 'action',
          width: 160,
          render: (h, params) => {
            return h('div', [
              h('Button', {
                props: {
                  type: 'primary',
                  size: 'small',
                },
                style: {
                  marginRight: '5px',
                  color: '#fff',
                },
                on: {
                  click: () => {
                    this.handleEdit(params.row);
                  },
                },
              }, '编辑'),
              h('Button', {
                props: {
                  type: 'error',
                  size: 'small',
                },
                style: {
                  color: '#fff',
                },
                on: {
                  click: () => {
                    this.handleDelete(params.row);
                  },
                },
              }, '删除'),
            ]);
          },
        },
      ],

      rules: {
        name: [{required: true, message: '请输入项目名称', trigger: 'blur'}],
        charger: [{required: true, message: '请选择讲师', trigger: 'change'}],
        started_at: [{required: true, message: '请选择开始日期', trigger: 'change'}],
      },

      // Course form validation rules
      courseRules: {
        name: [{required: true, message: '请输入课程名称', trigger: 'blur'}],
        charger: [{required: true, message: '请选择讲师', trigger: 'change'}],
        started_at: [{required: true, message: '请选择开始时间', trigger: 'change'}],
      },
    };
  },
  computed: {
    filteredProjects() {
      return this.projects;
    },
    allProjectsCount() {
      return this.stateCount.total || 0;
    },
  },
  mounted() {
    this.fetchData();
    this.fetchStateCount();
  },
  methods: {
    getInitialFormData() {
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);

      return {
        name: '',
        charger: '',
        desc: '',
        avatar: '',
        started_at: today,
        finished_at: nextMonth,
        state: '0',
      };
    },
    getStateCount(state) {
      return this.stateCount[state] || 0;
    },
    getStateColor(state) {
      const stateMap = {
        0: 'blue',
        1: 'green',
        2: 'default',
      };
      return stateMap[state] || 'default';
    },
    getStateLabel(state) {
      const stateMap = {
        0: '未开始',
        1: '进行中',
        2: '已结束',
      };
      return stateMap[state] || state || '未知';
    },
    handleStateChange(state) {
      this.currentState = state;
      this.page = 1; // 重置到第一页
      this.fetchData();
    },
    handleSearch() {
      this.page = 1; // 重置到第一页
      this.fetchData();
    },
    async fetchStateCount() {
      try {
        const response = await this.$api.get('/study/projects', {
          params: {
            q: 'count',
            count_by: 'state',
          },
        });
        this.stateCount = response.data.data || {};
      } catch (error) {
        console.error('获取项目状态统计失败', error);
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await this.$api.get('/study/projects', {
          params: {
            q: 'page',
            page: this.page,
            pagesize: this.pageSize,
            search: this.searchQuery,
            state: this.currentState !== 'all' ? this.currentState : undefined,
            order: 'created_at',
            desc: 1,
          },
        });
        this.projects = response.data.data.list || [];
        this.total = response.data.data.count || 0;
      } catch (error) {
        this.$Message.error('获取项目列表失败');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async showProjectDetail(project) {
      this.currentProject = project;
      this.drawerVisible = true;

      // 获取项目详情和关联班级
      this.classesLoading = true;
      try {
        const response = await this.$api.get(`/study/projects/${project.id}?related=classes`);
        this.currentProject = response.data.data;
        const classes = response.data.data.classes || [];
        classes.forEach((item) => {
          item.avatar = item.avatar || (this.currentProject ? this.currentProject.avatar : '');
          item.charger = item.charger || (this.currentProject ? this.currentProject.charger : '');
        });
        this.projectClasses = classes;
      } catch (error) {
        this.$Message.error('获取项目详情失败');
        console.error(error);
      } finally {
        this.classesLoading = false;
      }
    },
    handleTableEvent(e) {
      const {type, data} = e;
      switch (type) {
        case 'page-change':
          this.page = data;
          this.fetchData();
          break;
        case 'page-size-change':
          this.pageSize = data;
          this.page = 1;
          this.fetchData();
          break;
      }
    },
    handleAdd() {
      this.modalTitle = '添加项目';
      this.formData = this.getInitialFormData();
      this.currentId = null;
      this.modalVisible = true;
    },
    async handleEdit(record) {
      this.modalTitle = '编辑项目';
      this.currentId = record.id;

      try {
        // 获取详细信息
        const response = await this.$api.get(`/study/projects/${record.id}?related=classes`);
        this.old = response.data.data;
        this.formData = {...response.data.data};

        // 转换日期格式为 Date 对象
        if (this.formData.started_at) {
          this.formData.started_at = new Date(this.formData.started_at);
        }
        if (this.formData.finished_at) {
          this.formData.finished_at = new Date(this.formData.finished_at);
        }

        this.modalVisible = true;
      } catch (error) {
        this.$Message.error('获取项目详情失败');
        console.error(error);
      }
    },
    async handleDelete(record) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除项目 "${record.name}" 吗？此操作不可恢复，关联的班级数据也将被删除。`,
        onOk: async () => {
          try {
            await this.$api.delete(`/study/projects/${record.id}`);
            this.$Message.success('删除成功');
            this.fetchData();
            this.fetchStateCount(); // 更新统计数据
          } catch (error) {
            this.$Message.error('删除失败');
            console.error(error);
          }
        },
      });
    },
    async handleSubmit() {
      try {
        await this.$refs.formRef.validate();
        this.confirmLoading = true;

        // 准备数据
        const data = {...this.formData};

        // 转换日期格式为字符串 YYYY-MM-DD
        if (data.started_at instanceof Date) {
          data.started_at = this.formatDateForAPI(data.started_at);
        }
        if (data.finished_at instanceof Date) {
          data.finished_at = this.formatDateForAPI(data.finished_at);
        }

        const updateData = this.$util.gpt_compare_patch_object(this.old, data);
        if (Object.keys(updateData).length > 0) {
          if (this.currentId) {
            // 更新
            await this.$api.patch(`/study/projects/${this.currentId}`, updateData);
            this.$Message.success('更新成功');
          } else {
          // 新增
            await this.$api.post('/study/projects', data);
            this.$Message.success('添加成功');
          }
        }

        this.modalVisible = false;
        this.fetchData();
        this.fetchStateCount(); // 更新统计数据
      } catch (error) {
        this.$Message.error(error.message || '提交失败');
        console.error(error);
      } finally {
        this.confirmLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return `${this.formatDate(dateString)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    },
    formatDateForAPI(date) {
      if (!date) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    // Add new method to initialize course form
    getInitialCourseFormData() {
      const now = new Date();

      return {
        name: '',
        charger: '',
        desc: '',
        address: '',
        started_at: now,
        duration: 60,
        video_url: '',
        state: '0',
        project_id: '',
      };
    },

    // Add method to handle adding a new course
    handleAddCourse() {
      if (!this.currentProject) return;

      this.courseModalTitle = '添加课程';
      this.courseFormData = this.getInitialCourseFormData();
      this.courseFormData.project_id = this.currentProject.id;
      this.currentCourseId = null;
      this.courseModalVisible = true;
    },

    // Add method to handle editing a course
    handleEditCourse(course) {
      this.courseModalTitle = '编辑课程';
      this.currentCourseId = course.id;

      // Clone the course data
      this.courseFormData = {...course};

      // Convert string dates to Date objects
      if (this.courseFormData.started_at) {
        this.courseFormData.started_at = new Date(this.courseFormData.started_at);
      }

      this.courseModalVisible = true;
    },

    // Add method to handle deleting a course
    handleDeleteCourse(course) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除课程 "${course.name}" 吗？此操作不可恢复。`,
        onOk: async () => {
          try {
            await this.$api.delete(`/study/classes/${course.id}`);
            this.$Message.success('删除成功');
            this.refreshProjectClasses();
          } catch (error) {
            this.$Message.error('删除失败');
            console.error(error);
          }
        },
      });
    },

    // Add method to handle submitting course form
    async handleCourseSubmit() {
      try {
        await this.$refs.courseFormRef.validate();
        this.courseConfirmLoading = true;

        // Prepare data
        const data = {...this.courseFormData};

        // Ensure project_id is set to current project
        data.project_id = this.currentProject.id;

        // Format date
        if (data.started_at instanceof Date) {
          // Format as ISO string
          data.started_at = data.started_at.toISOString();
        }

        // The video_url will be handled by the BaseFileUpload component
        // which will upload the file and return the URL

        if (this.currentCourseId) {
          // Update
          await this.$api.patch(`/study/classes/${this.currentCourseId}`, data);
          this.$Message.success('更新成功');
        } else {
          // Create
          await this.$api.post('/study/classes', data);
          this.$Message.success('添加成功');
        }

        this.courseModalVisible = false;
        this.refreshProjectClasses();
      } catch (error) {
        this.$Message.error(error.message || '提交失败');
        console.error(error);
      } finally {
        this.courseConfirmLoading = false;
      }
    },

    // Add method to refresh project classes
    async refreshProjectClasses() {
      if (!this.currentProject) return;

      this.classesLoading = true;
      try {
        const response = await this.$api.get(`/study/projects/${this.currentProject.id}?related=classes`);
        this.currentProject = response.data.data;
        this.projectClasses = response.data.data.classes || [];

        // Process classes - inherit avatar and charger from project when not set
        this.projectClasses = this.projectClasses.map((item) => {
          return {
            ...item,
            avatar: item.avatar || (this.currentProject ? this.currentProject.avatar : ''),
            charger: item.charger || (this.currentProject ? this.currentProject.charger : ''),
          };
        });
      } catch (error) {
        this.$Message.error('获取课程列表失败');
        console.error(error);
      } finally {
        this.classesLoading = false;
      }
    },
  },
};
</script>

<style>
.search-bar {
  border-bottom: 1px solid var(--border);
  padding: 15px;
  background: #fff;
  display: flex;
  align-items: center;
}

.status-filter {
  display: flex;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 4px;
  padding: 10px 15px;
}

.status-item {
  padding: 5px 12px;
  margin-right: 10px;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.3s;
  position: relative;
  margin-bottom: 5px;
}

.status-item:hover {
  background: #f0f0f0;
}

.status-item.active {
  background: #e6f7ff;
  color: #1890ff;
  font-weight: 500;
}

.status-item .count {
  font-size: 12px;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 0 6px;
  margin-left: 5px;
  color: #666;
}

.status-item.active .count {
  background: #1890ff;
  color: #fff;
}

.table-wrapper {
  position: relative;
  height: calc(100vh - 160px);
  overflow: auto;
  background: #fff;
  border-radius: 4px;
  margin:0 15px;
  width:calc(100% - 35px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 20px;
}

.form-section h3 {
  font-size: 16px;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.form-row {
  display: flex;
  margin: 0 -8px;
}

.form-item {
  padding: 0 8px;
  flex: 1;
}

/* 项目详情样式 */
.project-detail {
  padding: 0 20px;
}

.detail-header {
  display: flex;
  margin-bottom: 24px;
}

.project-avatar {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f0f0;
  margin-right: 20px;
  flex-shrink: 0;
}

.project-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: #999;
}

.project-info h2 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 20px;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.created-time {
  margin-left: 10px;
  font-size: 12px;
  color: #999;
}

.project-desc {
  color: #666;
  line-height: 1.5;
}

.info-section {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 24px;
}

.info-item {
  display: flex;
  margin-bottom: 10px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item .label {
  width: 80px;
  color: #666;
  flex-shrink: 0;
}

.loading-classes {
  padding: 40px 0;
  text-align: center;
  position: relative;
  height: 120px;
}

.empty-classes {
  padding: 40px 0;
  text-align: center;
}

.empty-placeholder {
  color: #999;
}

.empty-placeholder i {
  margin-bottom: 10px;
  color: #ccc;
}

.classes-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 20px;
}

.course-card {
  height: 100%;
  transition: all 0.3s;
}

.course-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.course-title {
  font-weight: bold;
  font-size: 16px;
  margin-right: 8px;
}

.course-info {
  margin-bottom: 12px;
}

.course-info-item {
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;
}

.course-info-item i {
  margin-right: 4px;
  font-size: 14px;
  color: #999;
}

.course-desc {
  border-top: 1px dashed #eee;
  padding-top: 8px;
  margin-top: 8px;
  color: #888;
  font-size: 13px;
}

.desc-content {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
}

/* Add New Course Card Styles */
.add-course-card {
  border: 2px dashed #dcdee2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fafafa;
  height: 100%;
}

.add-course-card:hover {
  border-color: #1890ff;
  background: #f0f9ff;
}

.add-course-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #999;
  padding: 20px 0;
}

.add-course-content i {
  margin-bottom: 8px;
  transition: all 0.3s;
}

.add-course-content p {
  margin: 0;
}

.add-course-card:hover .add-course-content {
  color: #1890ff;
}

/* Course Action Buttons */
.course-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.course-actions button {
  margin-left: 8px;
}

/* Course Card Adjustments for Actions */
.course-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.course-info, .course-desc {
  flex-grow: 1;
}
</style>
